import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Slide } from "../components/HeroSlide/HeroSlide";
import { EPISODES } from "shared/data/episodes";

interface SlideProps {
  slide: Slide
  badgeLabel: string
}

export const useGenerateSlides = (): SlideProps[] => {
  const { t } = useTranslation('home');
  const { upcomingEpisodes, latestEpisode, bonusEpisode } = useMemo(() => {
    const firstReleasedIndex = EPISODES.findIndex((episode) => !episode.upcoming);

    const hasReleased = firstReleasedIndex !== -1

    const firstReleased = hasReleased ? EPISODES[firstReleasedIndex] : undefined;

    const firstIsBonus = firstReleased?.bonus

    const secondReleased = hasReleased && EPISODES.length > firstReleasedIndex
      ? EPISODES[firstReleasedIndex + 1]
      : undefined

    return {
      upcomingEpisodes: EPISODES.slice(0, firstReleasedIndex === -1 ? undefined : firstReleasedIndex),
      latestEpisode: firstIsBonus ? secondReleased : firstReleased,
      bonusEpisode: firstIsBonus ? firstReleased : undefined,
    };
  }, []);

  const latestEpisodeSlide = useMemo(() => {
    if (!latestEpisode) return []

    return [{ 
      slide: {
        ...latestEpisode,
        primaryCta: {
          label: t('hero.listenToEpisode'),
          to: `/episodes/${latestEpisode.number}`
        },
        secondaryCta: {
          label: t('hero.viewShowNotes'),
          to: `/episodes/${latestEpisode.number}#show-notes`,
        }
      },
      badgeLabel: t('hero.badgeLatest'),
    }]
  },[latestEpisode, t])

  const bonusEpisodeSlide = useMemo(() => {
    if (!bonusEpisode) return []

    return [{
      slide: {
        ...bonusEpisode,
        primaryCta: {
          label: t('hero.listenToEpisode'),
          to: `/episodes/${bonusEpisode.number}`
        },
        secondaryCta: {
          label: t('hero.viewShowNotes'),
          to: `/episodes/${bonusEpisode.number}#show-notes`,
        }
      },
      badgeLabel: t('hero.badgeBonus'),
    }]
  },[bonusEpisode, t])

  const upcomingEpisodeSlides = useMemo(() => {
    return upcomingEpisodes.map((ep) => (
      {
        slide: ep,
        badgeLabel: t('hero.badgeUpcoming')
      }
    ))
  }, [upcomingEpisodes, t]);

  return [
    ...bonusEpisodeSlide,
    ...latestEpisodeSlide,
    ...upcomingEpisodeSlides
  ]
}